/**
 * Debounced autosave with explicit lifecycle states.
 *
 * Why: every keystroke firing a PUT was making typing feel laggy and
 * spawning racing writes. This hook batches changes for `debounceMs`,
 * then submits one PUT.
 *
 * States surfaced for UI ('Saved · 3s ago' indicator):
 *   - 'idle'    : nothing to save
 *   - 'pending' : user typed; waiting for debounce
 *   - 'saving'  : PUT in flight
 *   - 'saved'   : last save succeeded
 *   - 'error'   : last save failed (caller can show retry)
 *
 * Caller pattern:
 *   const { queue, flush, status, lastSavedAt, error } = useDebouncedAutosave({
 *       onSave: (delta) => updateEncounter(id, delta),
 *       enabled: canWrite && !isFinalized,
 *   });
 *   const patch = (delta) => { setLocal(merge(local, delta)); queue(delta); };
 *
 * `flush()` forces an immediate write (call before navigating away).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export default function useDebouncedAutosave({
    onSave,
    debounceMs = 600,
    enabled = true,
}) {
    const [status, setStatus] = useState('idle');
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [error, setError] = useState(null);

    const pendingRef = useRef(null);    // merged delta waiting to flush
    const timerRef = useRef(null);
    const inFlightRef = useRef(false);
    const enabledRef = useRef(enabled);

    useEffect(() => { enabledRef.current = enabled; }, [enabled]);

    const performSave = useCallback(async () => {
        if (!enabledRef.current) return;
        if (inFlightRef.current) return;
        const payload = pendingRef.current;
        if (!payload || Object.keys(payload).length === 0) return;
        pendingRef.current = null;
        inFlightRef.current = true;
        setStatus('saving');
        setError(null);
        try {
            await onSave(payload);
            setStatus('saved');
            setLastSavedAt(Date.now());
        } catch (err) {
            // Restore unsaved payload so the next flush still tries
            pendingRef.current = { ...(payload || {}), ...(pendingRef.current || {}) };
            setError(err);
            setStatus('error');
        } finally {
            inFlightRef.current = false;
            // If more changes accumulated during the in-flight save, kick another.
            if (pendingRef.current && Object.keys(pendingRef.current).length > 0) {
                if (timerRef.current) clearTimeout(timerRef.current);
                timerRef.current = setTimeout(performSave, 50);
            }
        }
    }, [onSave]);

    const queue = useCallback((delta) => {
        if (!enabledRef.current) return;
        if (!delta || Object.keys(delta).length === 0) return;
        pendingRef.current = { ...(pendingRef.current || {}), ...delta };
        setStatus('pending');
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(performSave, debounceMs);
    }, [debounceMs, performSave]);

    const flush = useCallback(async () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        await performSave();
    }, [performSave]);

    // Snapshot of edits typed but not yet persisted. Lets the caller re-apply
    // in-flight edits on top of a server response so a save that started
    // mid-word doesn't clobber characters (commonly a trailing space) typed
    // while the request was in flight.
    const getPending = useCallback(() => pendingRef.current || {}, []);

    // Clear timer on unmount
    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    const hasPending = Boolean(pendingRef.current) || status === 'pending' || status === 'saving';

    return { queue, flush, status, lastSavedAt, error, hasPending, getPending };
}
